/**
 * @module utils/helper
 * @description Cung cấp các hàm tiện ích và các hằng số cấu hình chung cho toàn bộ ứng dụng.
 */

import dayjs from "dayjs";

// --- Hàm tiện ích ---

export const getQuery = (params: Record<string, any>) => {
  const queryParts: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(item => {
        queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
      });
    } else if (value !== undefined && value !== null) {
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });

  return queryParts.length ? `?${queryParts.join('&')}` : '';
}

/**
 * Chuyển đổi object sang FormData, hỗ trợ object lồng nhau, mảng và file.
 * @category Hàm tiện ích
 * @param obj Object nguồn
 * @param form FormData đã có (nếu có)
 * @param parentKey Key cha (dùng cho đệ quy)
 */
export function convertObjectToFormData(obj: any, form: FormData = new FormData(), parentKey?: string): FormData {
  if (obj === null || obj === undefined) return form;
  if (typeof obj !== 'object' || obj instanceof Date || obj instanceof File || obj instanceof Blob) {
    if (parentKey) form.append(parentKey, obj);
    return form;
  }
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    const fullKey = parentKey ? `${parentKey}[${key}]` : key;
    if (Array.isArray(value)) {
      value.forEach((v, idx) => {
        const arrayKey = `${fullKey}[${idx}]`;
        if (typeof v === 'object' && v !== null) {
          convertObjectToFormData(v, form, arrayKey);
        } else {
          form.append(arrayKey, v);
        }
      });
    } else if (typeof value === 'object' && value !== null && !(value instanceof File) && !(value instanceof Blob)) {
      convertObjectToFormData(value, form, fullKey);
    } else if (value !== undefined) {
      form.append(fullKey, value);
    }
  });
  return form;
}

/**
 * Loại bỏ các trường meta (created_by, created_date, modified_by, modified_date) và các trường tùy chọn khỏi một object.
 * @category Hàm tiện ích
 * @template T - Kiểu dữ liệu của object.
 * @param {T} obj - Object cần xử lý.
 * @param {string[]} [fields] - Mảng các trường tùy chọn cần loại bỏ.
 * @returns {Omit<T, 'created_by' | 'created_date' | 'modified_by' | 'modified_date'>} - Object mới đã được loại bỏ các trường.
 */
export function omitMetaFields<T extends object>(obj: T, fields?: string[]): Omit<T, 'created_by' | 'created_date' | 'modified_by' | 'modified_date'> {
  const { created_by, created_date, modified_by, modified_date, ...rest } = obj as any;
  if (fields) {
    fields.forEach((field) => {
      delete rest[field];
    });
  }
  return rest;
}

/**
 * Chuyển đổi một URL hình ảnh sang URL thumbnail tương ứng.
 * @category Hàm tiện ích
 * @param {string} url - URL gốc.
 * @returns {string} - URL thumbnail.
 */
export function convertToThumbnailUrl(url: string): string {
  try {
    const u = new URL(url);
    // Lấy phần cuối của path (vd: /cntt => cntt)
    const parts = u.pathname.split('/').filter(Boolean);
    if (parts.length === 0) return url;
    const name = parts[parts.length - 1];
    return `${u.origin}/thumbnail/${name}`;
  } catch (e) {
    return url;
  }
}

/**
 * @typedef {Object} SelectOption
 * @property {string} value - Giá trị của tùy chọn.
 * @property {string} label - Nhãn hiển thị của tùy chọn.
 */

/**
 * Chuyển đổi một mảng dữ liệu thành danh sách các tùy chọn cho select box, thêm tùy chọn "Tất cả".
 * @category Hàm tiện ích
 * @param {any[]} data - Mảng dữ liệu đầu vào. Mỗi phần tử phải có `value` và `label`.
 * @returns {SelectOption[]} - Mảng các tùy chọn cho select box.
 */
export const convertToSelectOptionsFilter = (data: any[]) => {
  if (data.length === 0) return [];
  let res = data.map((item) => ({
    value: String(item.value),
    label: item.label,
  }));
  res.unshift({ value: "all", label: "Tất cả" });
  return res;
}

// --- Danh sách Trạng thái ---

/**
 * @typedef {Object} StatusItem
 * @property {number} value - Giá trị của trạng thái.
 * @property {string} label - Nhãn hiển thị của trạng thái.
 * @property {string} color - Màu sắc liên quan đến trạng thái.
 */

/**
 * Danh sách các trạng thái của thiết bị.
 * @category Danh sách Trạng thái
 * @type {StatusItem[]}
 */
export const listDeviceStatus = [
  {
    value: 0,
    label: 'homePage.measuring',
    color: 'green'
  },
  {
    value: 1,
    label: 'homePage.calibrating',
    color: 'yellow'
  },
  {
    value: 2,
    label: 'homePage.deviceError',
    color: 'red'
  },
  {
    value: 4,
    label: 'homePage.signalLost',
    color: 'gray'
  },
]

/**
 * Danh sách các trạng thái chung (ví dụ: cho thông s�� đo).
 * @category Danh sách Trạng thái
 * @type {StatusItem[]}
 */
export const listStatus = [
  {
    value: 0,
    label: 'homePage.withinThreshold',
    color: 'green'
  },
  {
    value: 1,
    label: 'homePage.nearThreshold',
    color: 'yellow'
  },
  {
    value: 2,
    label: 'homePage.exceedThreshold',
    color: 'red'
  },
  {
    value: 4,
    label: 'homePage.signalLost',
    color: 'gray'
  },
]

/**
 * Danh sách các loại sự cố.
 * @category Danh sách Trạng thái
 * @type {StatusItem[]}
 */
export const listProblemType = [
  {
    value: 1,
    label: 'Mất tín hiệu',
    color: 'green'
  },
  {
    value: 2,
    label: 'Lỗi thiết bị',
    color: 'yellow'
  },
  {
    value: 3,
    label: 'Khác',
    color: 'red'
  },
]

/**
 * @typedef {Object} StatisticStatusItem
 * @property {string} value - Giá trị của trạng thái.
 * @property {string} label - Nhãn hiển thị của trạng thái.
 * @property {string} color - Màu sắc liên quan đến trạng thái.
 */

/**
 * Danh sách các trạng thái cho dữ liệu thống kê.
 * @category Danh sách Trạng thái
 * @type {StatisticStatusItem[]}
 */
export const listStatisticStatus = [
  {
    value: '0',
    label: 'Trong ngưỡng',
    color: 'green'
  },
  {
    value: '1',
    label: 'Chuẩn bị vượt ngưỡng',
    color: 'yellow'
  },
  {
    value: '2',
    label: 'Vượt ngưỡng',
    color: 'red'
  }
]

// --- Cấu hình Thời gian ---

const today = dayjs();

/**
 * @typedef {Object} RangeDateItem
 * @property {number} value - Giá trị định danh.
 * @property {string} from - Ngày bắt đầu (YYYY-MM-DD HH:mm).
 * @property {string} to - Ngày kết thúc (YYYY-MM-DD HH:mm).
 * @property {string} label - Nhãn hiển thị.
 */

/**
 * Danh sách các khoảng thời gian được định nghĩa trước.
 * @category Cấu hình Thời gian
 * @type {RangeDateItem[]}
 */
export const listRangeDate = [
  {
    value: 1,
    from: today.subtract(1, 'day').format('YYYY-MM-DD HH:mm'),
    to: today.format('YYYY-MM-DD HH:mm'),
    label: '24 giờ qua',
  },
  {
    value: 2,
    from: today.subtract(7, 'day').format('YYYY-MM-DD HH:mm'),
    to: today.format('YYYY-MM-DD HH:mm'),
    label: '7 ngày qua',
  },
  {
    value: 3,
    from: today.subtract(15, 'day').format('YYYY-MM-DD HH:mm'),
    to: today.format('YYYY-MM-DD HH:mm'),
    label: '15 ngày qua',
  },
  { value: 4, from: '', to: '', label: 'Tùy chọn' },
]

export const listDateFilter = [
  {
    value: 1,
    from: today.format('YYYY-MM-DD 00:00'),
    to: today.format('YYYY-MM-DD HH:mm'),
    label: 'Trong ngày',
  },
  { value: 4, from: '', to: '', label: 'Tùy chọn' },
]


// --- Cấu hình Xuất file ---

/**
 * @typedef {Object} ExportTypeItem
 * @property {string} value - Giá trị (excel, pdf).
 * @property {string} label - Nhãn hiển thị.
 */

/**
 * Danh sách các loại file có thể xuất.
 * @category Cấu hình Xuất file
 * @type {ExportTypeItem[]}
 */
export const listExportType = [
  {
    value: 'excel',
    label: 'Excel',
  },
  {
    value: 'pdf',
    label: 'PDF',
  },
]
